/**
 * DrainageUtilities - Represents drainage and utilities infrastructure information.
 * 
 * NOTE: Property names in this class match the external data source schema.
 * They use PascalCase and UPPER_SNAKE_CASE to maintain direct mapping compatibility.
 */
export class DrainageUtilities {
  ActiveReference: string;
  ActiveStationOffsetReference: string;
  BaselineOffset: string;
  BaselineStation: string;
  BypassedFlow: string;
  CapturedFlow: string;
  CIFID: string;
  CurbOpeningLength: string;
  DelineationReference: string;
  Depth: string;
  Diameter: string;
  ECClassID: string;
  ECInstanceID: string;
  Element: string;
  ElementType: string;
  ElevationIsInvert: string;
  GrateLength: string;
  GrateWidth: string;
  GroundElevation: string;
  IndependentConduitElevations: string;
  InvertElevation: string;
  IsActive: string;
  Length: string;
  LongitudinalSlope: string;
  MatchSlopeOfConduit: string;
  NetworkType: string;
  OrientTopToSurface: string;
  PROPOnlyIncludeContributingSlopes: string;
  PROPOwner: string;
  PROPPROPCrosssectionOffset: string;
  RimElevation: string;
  RoadCrossSlopeDistance: string;
  SetOutElevation: string;
  SetOutX: string;
  SetOutY: string;
  Spread: string;
  UseLongitudinalSlope: string;
  UserDefinedElementType: string;
  UserDefinedNetworkType: string;
  UseRoadSlope: string;
  Width: string;

  /**
   * Creates a new DrainageUtilities instance
   * @param data - Object containing drainage utilities properties
   */
  constructor(data: {
    ActiveReference: string;
    ActiveStationOffsetReference: string;
    BaselineOffset: string;
    BaselineStation: string;
    BypassedFlow: string;
    CapturedFlow: string;
    CIFID: string;
    CurbOpeningLength: string;
    DelineationReference: string;
    Depth: string;
    Diameter: string;
    ECClassID: string;
    ECInstanceID: string;
    Element: string;
    ElementType: string;
    ElevationIsInvert: string;
    GrateLength: string;
    GrateWidth: string;
    GroundElevation: string;
    IndependentConduitElevations: string;
    InvertElevation: string;
    IsActive: string;
    Length: string;
    LongitudinalSlope: string;
    MatchSlopeOfConduit: string;
    NetworkType: string;
    OrientTopToSurface: string;
    PROPOnlyIncludeContributingSlopes: string;
    PROPOwner: string;
    PROPPROPCrosssectionOffset: string;
    RimElevation: string;
    RoadCrossSlopeDistance: string;
    SetOutElevation: string;
    SetOutX: string;
    SetOutY: string;
    Spread: string;
    UseLongitudinalSlope: string;
    UserDefinedElementType: string;
    UserDefinedNetworkType: string;
    UseRoadSlope: string;
    Width: string;
  }) {
    this.ActiveReference = data.ActiveReference;
    this.ActiveStationOffsetReference = data.ActiveStationOffsetReference;
    this.BaselineOffset = data.BaselineOffset;
    this.BaselineStation = data.BaselineStation;
    this.BypassedFlow = data.BypassedFlow;
    this.CapturedFlow = data.CapturedFlow;
    this.CIFID = data.CIFID;
    this.CurbOpeningLength = data.CurbOpeningLength;
    this.DelineationReference = data.DelineationReference;
    this.Depth = data.Depth;
    this.Diameter = data.Diameter;
    this.ECClassID = data.ECClassID;
    this.ECInstanceID = data.ECInstanceID;
    this.Element = data.Element;
    this.ElementType = data.ElementType;
    this.ElevationIsInvert = data.ElevationIsInvert;
    this.GrateLength = data.GrateLength;
    this.GrateWidth = data.GrateWidth;
    this.GroundElevation = data.GroundElevation;
    this.IndependentConduitElevations = data.IndependentConduitElevations;
    this.InvertElevation = data.InvertElevation;
    this.IsActive = data.IsActive;
    this.Length = data.Length;
    this.LongitudinalSlope = data.LongitudinalSlope;
    this.MatchSlopeOfConduit = data.MatchSlopeOfConduit;
    this.NetworkType = data.NetworkType;
    this.OrientTopToSurface = data.OrientTopToSurface;
    this.PROPOnlyIncludeContributingSlopes = data.PROPOnlyIncludeContributingSlopes;
    this.PROPOwner = data.PROPOwner;
    this.PROPPROPCrosssectionOffset = data.PROPPROPCrosssectionOffset;
    this.RimElevation = data.RimElevation;
    this.RoadCrossSlopeDistance = data.RoadCrossSlopeDistance;
    this.SetOutElevation = data.SetOutElevation;
    this.SetOutX = data.SetOutX;
    this.SetOutY = data.SetOutY;
    this.Spread = data.Spread;
    this.UseLongitudinalSlope = data.UseLongitudinalSlope;
    this.UserDefinedElementType = data.UserDefinedElementType;
    this.UserDefinedNetworkType = data.UserDefinedNetworkType;
    this.UseRoadSlope = data.UseRoadSlope;
    this.Width = data.Width;
  }

  /**
   * Creates a DrainageUtilities from a plain object (useful for deserialization)
   * @param obj - Plain object with drainage utilities data
   * @returns New DrainageUtilities instance
   */
  static fromObject(obj: Partial<DrainageUtilities>): DrainageUtilities {
    return new DrainageUtilities({
      ActiveReference: obj.ActiveReference ?? "",
      ActiveStationOffsetReference: obj.ActiveStationOffsetReference ?? "",
      BaselineOffset: obj.BaselineOffset ?? "",
      BaselineStation: obj.BaselineStation ?? "",
      BypassedFlow: obj.BypassedFlow ?? "",
      CapturedFlow: obj.CapturedFlow ?? "",
      CIFID: obj.CIFID ?? "",
      CurbOpeningLength: obj.CurbOpeningLength ?? "",
      DelineationReference: obj.DelineationReference ?? "",
      Depth: obj.Depth ?? "",
      Diameter: obj.Diameter ?? "",
      ECClassID: obj.ECClassID ?? "",
      ECInstanceID: obj.ECInstanceID ?? "",
      Element: obj.Element ?? "",
      ElementType: obj.ElementType ?? "",
      ElevationIsInvert: obj.ElevationIsInvert ?? "",
      GrateLength: obj.GrateLength ?? "",
      GrateWidth: obj.GrateWidth ?? "",
      GroundElevation: obj.GroundElevation ?? "",
      IndependentConduitElevations: obj.IndependentConduitElevations ?? "",
      InvertElevation: obj.InvertElevation ?? "",
      IsActive: obj.IsActive ?? "",
      Length: obj.Length ?? "",
      LongitudinalSlope: obj.LongitudinalSlope ?? "",
      MatchSlopeOfConduit: obj.MatchSlopeOfConduit ?? "",
      NetworkType: obj.NetworkType ?? "",
      OrientTopToSurface: obj.OrientTopToSurface ?? "",
      PROPOnlyIncludeContributingSlopes: obj.PROPOnlyIncludeContributingSlopes ?? "",
      PROPOwner: obj.PROPOwner ?? "",
      PROPPROPCrosssectionOffset: obj.PROPPROPCrosssectionOffset ?? "",
      RimElevation: obj.RimElevation ?? "",
      RoadCrossSlopeDistance: obj.RoadCrossSlopeDistance ?? "",
      SetOutElevation: obj.SetOutElevation ?? "",
      SetOutX: obj.SetOutX ?? "",
      SetOutY: obj.SetOutY ?? "",
      Spread: obj.Spread ?? "",
      UseLongitudinalSlope: obj.UseLongitudinalSlope ?? "",
      UserDefinedElementType: obj.UserDefinedElementType ?? "",
      UserDefinedNetworkType: obj.UserDefinedNetworkType ?? "",
      UseRoadSlope: obj.UseRoadSlope ?? "",
      Width: obj.Width ?? "",
    });
  }

  /**
   * Converts the DrainageUtilities to a plain object (useful for serialization)
   * @returns Plain object representation
   */
  toObject(): Record<string, unknown> {
    return {
      ActiveReference: this.ActiveReference,
      ActiveStationOffsetReference: this.ActiveStationOffsetReference,
      BaselineOffset: this.BaselineOffset,
      BaselineStation: this.BaselineStation,
      BypassedFlow: this.BypassedFlow,
      CapturedFlow: this.CapturedFlow,
      CIFID: this.CIFID,
      CurbOpeningLength: this.CurbOpeningLength,
      DelineationReference: this.DelineationReference,
      Depth: this.Depth,
      Diameter: this.Diameter,
      ECClassID: this.ECClassID,
      ECInstanceID: this.ECInstanceID,
      Element: this.Element,
      ElementType: this.ElementType,
      ElevationIsInvert: this.ElevationIsInvert,
      GrateLength: this.GrateLength,
      GrateWidth: this.GrateWidth,
      GroundElevation: this.GroundElevation,
      IndependentConduitElevations: this.IndependentConduitElevations,
      InvertElevation: this.InvertElevation,
      IsActive: this.IsActive,
      Length: this.Length,
      LongitudinalSlope: this.LongitudinalSlope,
      MatchSlopeOfConduit: this.MatchSlopeOfConduit,
      NetworkType: this.NetworkType,
      OrientTopToSurface: this.OrientTopToSurface,
      PROPOnlyIncludeContributingSlopes: this.PROPOnlyIncludeContributingSlopes,
      PROPOwner: this.PROPOwner,
      PROPPROPCrosssectionOffset: this.PROPPROPCrosssectionOffset,
      RimElevation: this.RimElevation,
      RoadCrossSlopeDistance: this.RoadCrossSlopeDistance,
      SetOutElevation: this.SetOutElevation,
      SetOutX: this.SetOutX,
      SetOutY: this.SetOutY,
      Spread: this.Spread,
      UseLongitudinalSlope: this.UseLongitudinalSlope,
      UserDefinedElementType: this.UserDefinedElementType,
      UserDefinedNetworkType: this.UserDefinedNetworkType,
      UseRoadSlope: this.UseRoadSlope,
      Width: this.Width,
    };
  }
}
